/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { BenefitType, BenefitTypeDisplayTextMap } from '@strata/constants';
import { formatDate, formatNameLastFirst } from '@strata/helper-functions';
import { ReferralChatIcon } from '@strata/react-chat';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    ColumnDef,
    ColumnDefGeneric,
    FilterCategory,
    GridWithFilters,
    ProductCell,
    SecondaryData,
} from '@strata/react-grid';
import { PatientInfoList } from '@strata/react-label-value-pairs';
import { FirstFillStatusIcon, Icon } from '@strata/react-status-icons';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import { isTaskTodo, WorklistTask } from '@strata/react-worklist';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { ReferralRowMenu } from 'components/wonReferrals/ReferralRowMenu';
import { useWorklistTasks } from 'services/APIHooks';
import { AppRoutes } from 'services/routes';
import { ReferralConversationCounts } from 'services/userSupportHelpers';
import { useAppSelector } from 'store';
import {
    useGetPagedReferralsQuery,
    useUpdateFirstFillInProgressMutation,
    WonReferralSummary,
} from 'store/api/performanceApi';
import { useGetPayerSegmentsQuery } from 'store/api/referenceApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';

const initialSortProperties = [{ id: 'referral_start', desc: true }];

const isRowDisabled = (
    status: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses],
) =>
    (
        [
            ReferralStatusIndicator.Statuses.CANCELLED,
        ] as (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses][]
    ).includes(status);

const FirstFillStatusToSetInProgressMap: Partial<
    Record<
        (typeof FirstFillStatusIcon.Statuses)[keyof typeof FirstFillStatusIcon.Statuses],
        { newStatus: string; newFirstFillInProgressValue: boolean }
    >
> = {
    [FirstFillStatusIcon.Statuses.PEND_FILL]: {
        newStatus: FirstFillStatusIcon.StatusTextMap[FirstFillStatusIcon.Statuses.IN_PROG](),
        newFirstFillInProgressValue: true,
    },
    [FirstFillStatusIcon.Statuses.IN_PROG]: {
        newStatus: FirstFillStatusIcon.StatusTextMap[FirstFillStatusIcon.Statuses.PEND_FILL](),
        newFirstFillInProgressValue: false,
    },
} as const;

/* eslint-disable react/prop-types */
const FirstFillStatusCell: ColumnDefGeneric<WonReferralSummary, 'friendly_id'>['cell'] = ({
    row,
}) => {
    const [updateFirstFillInProgress] = useUpdateFirstFillInProgressMutation();

    const { newStatus, newFirstFillInProgressValue } =
        (row.original.first_fill_status &&
            FirstFillStatusToSetInProgressMap?.[row.original.first_fill_status]) ??
        {};
    if (!newStatus) {
        return null;
    }
    return (
        <ReferralRowMenu
            menuItemArray={[
                {
                    text: `Set to ${newStatus}`,
                    onItemClick: () => {
                        (() =>
                            updateFirstFillInProgress({
                                referralId: row.original.referral_id,
                                firstFillInProgress: newFirstFillInProgressValue!,
                            }))();
                    },
                },
            ]}
        />
    );
};
/* eslint-enable react/prop-types */

const columns = ({
    referralConversationCounts,
    worklistTasks,
}: {
    referralConversationCounts?: ReferralConversationCounts;
    worklistTasks?: WorklistTask[];
}): ColumnDef<WonReferralSummary>[] => [
    {
        accessorKey: 'product_name',
        header: 'Product',
        size: 190,
        cell: ({ row, getValue }) => (
            <ProductCell product={getValue()} isBrand={row.original.is_brand} primary />
        ),
    },
    {
        accessorKey: 'last_name',
        header: 'Patient',
        size: 120,
        cell: ({ getValue, row }) => (
            <SecondaryData data={formatNameLastFirst(getValue(), row.original.first_name)} />
        ),
    },
    {
        accessorKey: 'member_id',
        header: 'Member ID',
        size: 120,
    },
    {
        accessorKey: 'segment_name',
        header: 'Health Plan',
        size: 120,
    },
    {
        accessorKey: 'value_cases',
        header: 'Attributes',
        size: 80,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <PatientInfoList
                values={[
                    ...(getValue() ?? []),
                    ...(row.original.has_copay_assistance
                        ? [{ strat_rule_name: PatientInfoList.Types.COPAY }]
                        : []),
                ]}
                incentiveTotal={row.original.incentive_total}
                iconsOnly
            />
        ),
    },
    {
        accessorKey: 'first_fill_status',
        header: () => (
            <Icon
                icon={IconNames.HIGH_PRIORITY}
                tooltipContent={
                    <div>
                        <div>First fill status indicator.</div>
                        <div>1. Pending First Fill</div>
                        <div>2. First Fill in Progress - Optional to track</div>
                        <div>3. Claim Received - Automatic update from a paid claim</div>
                    </div>
                }
            />
        ),
        size: 40,
        cell: ({ getValue }) => <FirstFillStatusIcon status={getValue()} showActionDetail />,
    },
    {
        // This key is not used to actually access any data
        accessorKey: 'is_brand',
        header: () => <Icon icon={IconNames.FORM} tooltipContent="Worklist tasks indicator." />,
        enableSorting: false,
        size: 40,
        cell: ({ row }) => {
            const tasks = worklistTasks?.filter(
                (task) => task.referral_id === row.original.referral_id && isTaskTodo(task),
            );

            let intent;
            let statusText = 'upcoming';
            if (tasks?.some((task) => dayjs().isAfter(task.due_date))) {
                intent = Icon.Intent.ERROR;
                statusText = 'that are overdue';
            } else if (tasks?.some((task) => dayjs().isAfter(task.warning_date))) {
                intent = Icon.Intent.WARNING;
                statusText = 'due soon';
            }

            return (tasks ?? []).length > 0 ? (
                <Icon
                    icon={IconNames.FORM}
                    intent={intent}
                    title="Worklist Tasks"
                    tooltipContent={`This referral has worklist tasks ${statusText}.`}
                />
            ) : null;
        },
    },
    {
        accessorKey: 'referral_id',
        header: () => (
            <Icon
                icon={IconNames.CHAT}
                tooltipContent="Open conversations and unread messages indicator."
            />
        ),
        enableSorting: false,
        size: 40,
        cell: ({ getValue }) => (
            <ReferralChatIcon
                openConversations={referralConversationCounts?.[getValue()]?.open}
                unreadMessages={referralConversationCounts?.[getValue()]?.unread}
            />
        ),
    },
    {
        accessorKey: 'referral_start',
        header: 'Servicing Start Date',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'referral_status',
        header: 'Referral Status',
        size: 130,
        cell: ({ getValue }) => <ReferralStatusIndicator status={getValue()} />,
    },
    {
        // This key is not used to actually access any data
        accessorKey: 'friendly_id',
        header: '',
        size: 45,
        enableSorting: false,
        cell: FirstFillStatusCell,
    },
];

export const WonReferralsListPage = () => {
    const navigate = useNavigate();
    const loggedInSupplierName = useLoggedInSupplierName();

    const worklistTasks = useWorklistTasks();

    const referralConversationCounts = useAppSelector(
        (state) => state.userSupport.referralConversationCounts,
    );

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
        updateCurrentPage,
    } = useGridState(Grids.WON_REFERRALS, initialSortProperties);

    const apiQueryParams = {
        page,
        searchText: search,
        sort,
        ...activeFilters,
    };

    const { data: firstPageData, isFetching: isFirstPageDataFetching } = useGetPagedReferralsQuery({
        ...apiQueryParams,
        page: 0,
    });
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } =
        useGetPagedReferralsQuery(apiQueryParams);

    const { data: segmentsData } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = segmentsData ?? {};

    const filterCategories: FilterCategory<WonReferralSummary>[] = [
        {
            key: 'segment_id',
            title: 'Health Plan',
            customValues: firstPageData?.filterOptions?.segment_id?.map((option) => ({
                value: option,
                text: payerSegmentMap?.[option]?.name,
            })),
        },
        {
            key: 'product_name',
            title: 'Product',
            customValues: firstPageData?.filterOptions?.product_name?.map((option) => ({
                value: option,
            })),
        },
        {
            key: 'benefit',
            title: 'Benefit',
            customValues: firstPageData?.filterOptions?.benefit?.map((option) => ({
                value: option,
                text: BenefitTypeDisplayTextMap[option as BenefitType],
            })),
        },
        {
            key: 'referral_status',
            title: 'Referral Status',
            customValues: firstPageData?.filterOptions?.referral_status?.map((option) => ({
                value: option,
                text: ReferralStatusIndicator.StatusMessages[
                    option as (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses]
                ],
            })),
        },
        {
            key: 'first_fill_status',
            title: 'First Fill Status',
            customValues: firstPageData?.filterOptions?.first_fill_status?.map((option) => ({
                value: option,
                text: FirstFillStatusIcon.StatusTextMap?.[
                    option as (typeof FirstFillStatusIcon.Statuses)[keyof typeof FirstFillStatusIcon.Statuses]
                ]?.(),
            })),
        },
        {
            key: 'value_case_name',
            title: 'Performance Requirements',
            customValues: firstPageData?.filterOptions?.value_case_name?.map((option) => ({
                value: option,
            })),
        },
    ];

    return (
        <FormPageContainer>
            <GridWithFilters
                title="My Referrals"
                secondaryTitle={loggedInSupplierName}
                activeFilters={activeFilters}
                columns={columns({ referralConversationCounts, worklistTasks })}
                currentPage={page}
                data={currentPageData?.referrals}
                enableSearch
                filterCategories={filterCategories}
                filterPaneRenderWidth={filterPaneWidth}
                isLoading={isFirstPageDataFetching || isCurrentPageDataFetching}
                manualSorting
                onFilter={updateGridActiveFilters}
                onFilterPaneResize={updateFilterPaneWidth}
                onPageChange={updateCurrentPage}
                onRowClick={(rowData) => {
                    if (!isRowDisabled(rowData.referral_status)) {
                        navigate(AppRoutes.wonReferralDetails(rowData.referral_id));
                    }
                }}
                onSearch={updateGridSearch}
                onSort={updateGridSort}
                rowCssClass={(rowData) =>
                    classNames({ 'griddle-row--disabled': isRowDisabled(rowData.referral_status) })
                }
                searchText={search}
                sortProperties={sort}
                totalCount={firstPageData?.count}
            />
        </FormPageContainer>
    );
};
